import { useEffect, useState } from "react";
import { Navigate } from "react-router";

import { miniApp } from "@tma.js/sdk-react";
import useEmblaCarousel from "embla-carousel-react";

import type { PlanId } from "shared/api/tribute/types";

import { BenefitsSlide } from "entities/benefits/benefits-slide";
import { MoreBenefitsSlide } from "entities/benefits/more-benefits-slide";
import { Header } from "pages/ui/header/header";
import { Layout } from "pages/ui/page";
import {
  clearAwaitingPayment,
  detectCurrency,
  formatPlanPrice,
  isAwaitingPaymentPersisted,
  isSubscriptionActive,
  markAwaitingPayment,
  openPaymentUrl,
} from "shared/api/tribute/helpers";
import { usePlansQuery } from "shared/api/tribute/use-plans-query";
import { useSubscribeMutation } from "shared/api/tribute/use-subscribe-mutation";
import { useSubscriptionQuery } from "shared/api/tribute/use-subscription-query";
import { useAuthData } from "shared/providers/auth/context";
import { Button } from "shared/ui/button";
import { CurrencySelect } from "shared/ui/currency-select";
import { cn } from "shared/utils/cn";

const SLIDE_PLANS: PlanId[] = ["standard", "pro"];
// Stop polling for the payment result after this long.
const AWAIT_PAYMENT_TIMEOUT_MS = 5 * 60 * 1000;

export const Start = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel();

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", () => setActiveSlide(emblaApi.selectedScrollSnap()));
  }, [emblaApi]);

  const authData = useAuthData();
  const plansQuery = usePlansQuery();
  const subscribeMutation = useSubscribeMutation();

  // Restored from localStorage: opening the Tribute checkout closes the mini
  // app, so a fresh launch resumes waiting for the payment result.
  const [isAwaitingPayment, setIsAwaitingPayment] = useState(
    isAwaitingPaymentPersisted
  );
  const subscriptionQuery = useSubscriptionQuery({
    refetchInterval: isAwaitingPayment ? 3000 : false,
  });
  const isActivated = isSubscriptionActive(subscriptionQuery.data);

  useEffect(() => {
    if (!isAwaitingPayment) return;
    const timeout = setTimeout(() => {
      setIsAwaitingPayment(false);
      clearAwaitingPayment();
    }, AWAIT_PAYMENT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isAwaitingPayment]);

  // The user relaunched after paying and the webhook has already landed:
  // auth data comes back with the plan set.
  const isAlreadySubscribed = authData.user.subscription !== "default";

  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  useEffect(() => {
    // Payment confirmed — the paywall's job is done.
    if (isAwaitingPayment && (isActivated || isAlreadySubscribed)) {
      setIsAwaitingPayment(false);
      clearAwaitingPayment();
      setIsPaymentComplete(true);
    }
  }, [isAwaitingPayment, isActivated, isAlreadySubscribed]);

  useEffect(() => {
    // Subscription confirmed — just close the mini app: it was opened from
    // the bot chat, so closing lands the user right back in it. Wait until
    // we are actually visible (the checkout TMA stacks on top of ours) so
    // the close isn't swallowed mid-transition.
    if (!isPaymentComplete) return;
    let timeout: number | undefined;
    const attempt = () => {
      timeout = window.setTimeout(() => miniApp.close.ifAvailable(), 150);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        attempt();
      }
    };
    if (document.visibilityState === "visible") {
      attempt();
    } else {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, [isPaymentComplete]);

  const [currency, setCurrency] = useState(detectCurrency);
  const slidePlanId = SLIDE_PLANS[activeSlide];
  const plan = plansQuery.data?.plans.find((p) => p.id === slidePlanId);
  const price = formatPlanPrice(plan, currency);
  const paymentsEnabled = Boolean(plansQuery.data?.paymentsEnabled);
  // A non-empty expiresAt means the user has already had a subscription (and
  // its one-time trial) before — the backend will not grant another one.
  const trialEligible = !subscriptionQuery.data?.expiresAt;

  const handleStart = async () => {
    try {
      const result = await subscribeMutation.mutateAsync({
        plan: slidePlanId,
        currency,
      });
      // Persist BEFORE opening: the checkout closes this mini app.
      markAwaitingPayment();
      setIsAwaitingPayment(true);
      openPaymentUrl(result.webappPaymentUrl, result.paymentUrl);
    } catch (error) {
      console.error(error);
    }
  };

  if (isPaymentComplete) {
    // The close request is on its way — show nothing but a spinner for the
    // fraction of a second the app stays on screen.
    return <Layout withPattern back={false} isLoading />;
  }

  if (isAlreadySubscribed && !isAwaitingPayment) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <Layout withPattern back={false}>
      <Header className="mt-7 mb-10" />
      <div className="flex grow flex-col">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex cursor-grab select-none">
            <BenefitsSlide />
            <MoreBenefitsSlide />
          </div>
        </div>
        <div className="mt-1 mb-3 flex justify-center gap-x-1.5">
          {SLIDE_PLANS.map((planId, i) => (
            <button
              key={planId}
              className={cn(
                "size-2 rounded-full transition-colors",
                i === activeSlide ? "bg-crystal-blue" : "bg-white/30"
              )}
              onClick={() => emblaApi?.scrollTo(i)}
            />
          ))}
        </div>

        <div className="mt-auto flex w-full flex-col justify-center gap-3 px-4.5">
          {paymentsEnabled ? (
            <>
              <div className="flex w-full gap-x-2">
                <CurrencySelect value={currency} onChange={setCurrency} />
                <Button
                  size="52"
                  isLoading={subscribeMutation.isPending}
                  className="min-w-0 flex-1"
                  onClick={handleStart}
                >
                  {trialEligible
                    ? `Try ${plan?.title ?? "Resonar"} for Free`
                    : `Subscribe to ${plan?.title ?? "Resonar"}`}
                </Button>
              </div>
              <div className="text-pale-grey flex w-full justify-center text-center text-sm leading-4.5 tracking-[0.23px]">
                {trialEligible
                  ? `7 days free, then ${price}/month.`
                  : `${price}/month.`}
              </div>
            </>
          ) : (
            <div className="text-pale-grey flex w-full justify-center text-center text-sm leading-4.5 tracking-[0.23px]">
              Payments are temporarily unavailable.
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
